/**
 * KOPS-584: Marketing Hub — ListReports Killer
 *
 * Agent-facing marketing content generator with 5 tabs:
 * 1. Content Library — browse/create shareable content by category
 * 2. Marketing Kits — per-listing content packages (My / Brokerage / Search)
 * 3. Market Reports — city-level market stats shareables
 * 4. My Posts — history of generated content + lead attribution
 * 5. Leads — marketing-sourced leads
 *
 * Follows the Market Analytics page pattern (navy+gold, split panel).
 */

import React, { useEffect, useState, useCallback } from 'react';
import axiosInstance from '../../helpers/api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBullhorn, faImage, faChartBar, faHistory, faUserPlus,
  faSearch, faDownload, faCopy, faSpinner,
  faBuilding, faGlobe, faUser, faCheck, faTimes, faRedo,
  faChevronLeft, faChevronRight, faMagic,
  faHome, faTag, faDollarSign, faCalendarAlt
} from '@fortawesome/free-solid-svg-icons';
import {
  faFacebook as faFacebookBrand,
  faInstagram as faInstagramBrand,
  faLinkedin as faLinkedinBrand
} from '@fortawesome/free-brands-svg-icons';

// ─── STYLES ────────────────────────────────────────────────────
const NAVY = '#0F1E35';
const NAVY2 = '#162844';
const NAVY3 = '#1E3557';
const GOLD = '#C9952A';
const GOLD2 = '#E8B44A';
const MUTED = '#8FA3BF';
const LIGHT_BG = '#eef1f6';
const GREEN = '#27ae60';
const RED = '#e74c3c';

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minHeight: 'calc(100vh - 120px)',
    background: LIGHT_BG,
    fontFamily: "'DM Sans', sans-serif",
  },
  header: {
    background: NAVY,
    padding: '20px 32px',
    borderBottom: `2px solid ${GOLD}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap' as const,
    gap: '12px',
  },
  headerTitle: {
    fontFamily: "'Bebas Neue', sans-serif",
    fontSize: '28px',
    color: '#fff',
    letterSpacing: '1px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  tabs: {
    display: 'flex',
    gap: '0',
    background: NAVY2,
    borderBottom: `1px solid ${NAVY3}`,
  },
  tab: {
    padding: '12px 24px',
    cursor: 'pointer',
    color: MUTED,
    fontSize: '14px',
    fontWeight: 600,
    borderBottom: '3px solid transparent',
    transition: 'all 0.2s',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  tabActive: {
    color: GOLD2,
    borderBottomColor: GOLD,
    background: 'rgba(201,149,42,0.08)',
  },
  content: {
    flex: 1,
    padding: '24px',
    overflow: 'auto',
  },
  card: {
    background: '#fff',
    borderRadius: '8px',
    border: '1px solid #e2e8f0',
    padding: '20px',
    marginBottom: '16px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
  },
  cardTitle: {
    fontFamily: "'Bebas Neue', sans-serif",
    fontSize: '20px',
    color: NAVY,
    letterSpacing: '0.5px',
    marginBottom: '16px',
    borderLeft: `3px solid ${GOLD}`,
    paddingLeft: '12px',
  },
  scopeToggle: {
    display: 'flex',
    gap: '8px',
    marginBottom: '20px',
  },
  scopeBtn: {
    padding: '8px 20px',
    borderRadius: '20px',
    border: `1px solid #e2e8f0`,
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: 600,
    background: '#fff',
    color: NAVY,
    transition: 'all 0.2s',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  scopeBtnActive: {
    background: NAVY,
    color: GOLD2,
    borderColor: GOLD,
  },
  listingRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    padding: '16px',
    borderBottom: '1px solid #f0f0f0',
    cursor: 'pointer',
    transition: 'background 0.15s',
  },
  listingPhoto: {
    width: '100px',
    height: '70px',
    borderRadius: '6px',
    objectFit: 'cover' as const,
    background: '#f0f0f0',
  },
  listingInfo: {
    flex: 1,
  },
  listingAddress: {
    fontWeight: 700,
    fontSize: '15px',
    color: NAVY,
  },
  listingMeta: {
    fontSize: '13px',
    color: '#6b7280',
    marginTop: '2px',
  },
  listingPrice: {
    fontFamily: "'Bebas Neue', sans-serif",
    fontSize: '22px',
    color: GOLD,
    letterSpacing: '1px',
  },
  generateBtn: {
    padding: '8px 20px',
    background: `linear-gradient(135deg, ${GOLD}, ${GOLD2})`,
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontWeight: 700,
    fontSize: '13px',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    transition: 'transform 0.15s',
  },
  searchBox: {
    display: 'flex',
    gap: '8px',
    marginBottom: '20px',
  },
  searchInput: {
    flex: 1,
    padding: '10px 16px',
    border: '1px solid #e2e8f0',
    borderRadius: '6px',
    fontSize: '14px',
    outline: 'none',
  },
  statusFilter: {
    display: 'flex',
    gap: '6px',
    marginBottom: '16px',
  },
  statusChip: {
    padding: '4px 14px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: 600,
    cursor: 'pointer',
    border: '1px solid #e2e8f0',
    background: '#fff',
    color: '#6b7280',
  },
  statusChipActive: {
    background: NAVY,
    color: GOLD2,
    borderColor: GOLD,
  },
  emptyState: {
    textAlign: 'center' as const,
    padding: '60px 20px',
    color: MUTED,
  },
  // Market Stats
  statCard: {
    background: NAVY,
    borderRadius: '8px',
    padding: '20px',
    textAlign: 'center' as const,
    border: `1px solid rgba(201,149,42,0.2)`,
  },
  statValue: {
    fontFamily: "'Bebas Neue', sans-serif",
    fontSize: '32px',
    color: GOLD2,
  },
  statLabel: {
    fontSize: '12px',
    color: MUTED,
    textTransform: 'uppercase' as const,
    letterSpacing: '1px',
    marginTop: '4px',
  },
  // History
  historyRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    padding: '12px 16px',
    borderBottom: '1px solid #f0f0f0',
  },
  historyThumb: {
    width: '60px',
    height: '60px',
    borderRadius: '6px',
    objectFit: 'cover' as const,
    background: '#f0f0f0',
  },
  // Wizard Modal
  modalOverlay: {
    position: 'fixed' as const,
    top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(0,0,0,0.6)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10000,
  },
  modalContent: {
    background: '#fff',
    borderRadius: '12px',
    width: '90%',
    maxWidth: '800px',
    maxHeight: '90vh',
    overflow: 'auto',
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
  },
  modalHeader: {
    padding: '20px 24px',
    borderBottom: `2px solid ${GOLD}`,
    background: NAVY,
    borderRadius: '12px 12px 0 0',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  modalBody: {
    padding: '24px',
  },
  stepIndicator: {
    display: 'flex',
    gap: '8px',
    marginBottom: '24px',
  },
  stepDot: {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '14px',
    fontWeight: 700,
    border: `2px solid #e2e8f0`,
    color: '#6b7280',
    background: '#fff',
  },
  stepDotActive: {
    background: GOLD,
    color: '#fff',
    borderColor: GOLD,
  },
  stepDotComplete: {
    background: GREEN,
    color: '#fff',
    borderColor: GREEN,
  },
  templateGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
    gap: '12px',
    marginTop: '16px',
  },
  templateCard: {
    border: '2px solid #e2e8f0',
    borderRadius: '8px',
    padding: '8px',
    cursor: 'pointer',
    textAlign: 'center' as const,
    transition: 'all 0.2s',
  },
  templateCardActive: {
    borderColor: GOLD,
    boxShadow: `0 0 0 2px ${GOLD}40`,
  },
  captionBox: {
    background: '#f9fafb',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    padding: '16px',
    fontFamily: 'monospace',
    fontSize: '13px',
    lineHeight: '1.6',
    minHeight: '120px',
    whiteSpace: 'pre-wrap' as const,
  },
  shareBtn: {
    padding: '10px 20px',
    borderRadius: '8px',
    border: '1px solid #e2e8f0',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
    fontWeight: 600,
    background: '#fff',
    transition: 'all 0.2s',
  },
};

// ─── TYPES ─────────────────────────────────────────────────────
interface Listing {
  listing_id: string;
  unparsed_address: string;
  city: string;
  state: string;
  zip_code: string;
  list_price: number;
  close_price?: number;
  bedrooms: number;
  bathrooms: number;
  living_area: number;
  days_on_market: number;
  standard_status: string;
  list_agent_name: string;
  list_office_name: string;
  photo_url?: string;
  photo_count?: number;
}

interface Template {
  id: string;
  contentType: string;
  name: string;
  layouts: string[];
}

interface MarketStats {
  location: string;
  active: { median_price: number; active_count: number; avg_dom: number; avg_sqft: number };
  closed: { closed_count: number; avg_close_price: number; sp_lp_ratio: number };
  newListings: number;
  cities: { city: string; listing_count: number }[];
}

interface HistoryItem {
  id: string;
  content_type: string;
  template_id: string;
  image_url: string;
  caption_text: string;
  listing_address: string;
  listing_price: number;
  city: string;
  lead_count: number;
  created_at: string;
}

type TabType = 'library' | 'kits' | 'market' | 'history' | 'leads';
type ScopeType = 'mine' | 'brokerage' | 'search';

// ─── COMPONENT ─────────────────────────────────────────────────
const MarketingHub: React.FC = () => {
  // Tab state
  const [activeTab, setActiveTab] = useState<TabType>('kits');

  // Listings state
  const [listings, setListings] = useState<Listing[]>([]);
  const [listingsLoading, setListingsLoading] = useState(false);
  const [listingsTotal, setListingsTotal] = useState(0);
  const [scope, setScope] = useState<ScopeType>('mine');
  const [statusFilter, setStatusFilter] = useState('Active');
  const [searchQuery, setSearchQuery] = useState('');

  // Templates state
  const [templates, setTemplates] = useState<Template[]>([]);

  // Market stats state
  const [marketStats, setMarketStats] = useState<MarketStats | null>(null);
  const [marketCity, setMarketCity] = useState('');
  const [cities, setCities] = useState<{ city: string; listing_count: number }[]>([]);
  const [marketLoading, setMarketLoading] = useState(false);

  // Subdivision state
  const [subdivisions, setSubdivisions] = useState<any[]>([]);
  const [subdivisionsLoading, setSubdivisionsLoading] = useState(false);
  const [selectedSubdivision, setSelectedSubdivision] = useState('');
  const [maxPriceFilter, setMaxPriceFilter] = useState('');

  // History state
  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Wizard state
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardStep, setWizardStep] = useState(1);
  const [selectedListing, setSelectedListing] = useState<Listing | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
  const [selectedTheme, setSelectedTheme] = useState('modern-dark');
  const [selectedLayout, setSelectedLayout] = useState('social-square');
  const [selectedContentType, setSelectedContentType] = useState('just-listed');
  const [caption, setCaption] = useState('');
  const [captionPlatform, setCaptionPlatform] = useState('instagram');
  const [generatedImage, setGeneratedImage] = useState('');
  const [portalLink, setPortalLink] = useState('');
  const [generating, setGenerating] = useState(false);
  const [captionLoading, setCaptionLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  // Facebook Ad campaign states
  const [showFbAdForm, setShowFbAdForm] = useState(false);
  const [fbAdAccounts, setFbAdAccounts] = useState<any[]>([]);
  const [selectedFbAccount, setSelectedFbAccount] = useState('');
  const [fbDailyBudget, setFbDailyBudget] = useState('10');
  const [fbDurationDays, setFbDurationDays] = useState('7');
  const [fbAdPublishing, setFbAdPublishing] = useState(false);
  const [fbAdResult, setFbAdResult] = useState<any>(null);

  // ─── DATA FETCHING ───────────────────────────────────────────

  const fetchListings = useCallback(async () => {
    setListingsLoading(true);
    try {
      const params: any = { scope, status: statusFilter, limit: 50 };
      if (scope === 'search' && searchQuery) params.search = searchQuery;
      const res = await axiosInstance.get('/marketing/listings', { params });
      setListings(res.data.data || []);
      setListingsTotal(res.data.total || 0);
    } catch (err) {
      console.error('Failed to load listings:', err);
      setListings([]);
    }
    setListingsLoading(false);
  }, [scope, statusFilter, searchQuery]);

  const fetchTemplates = async () => {
    try {
      const res = await axiosInstance.get('/marketing/templates');
      setTemplates(res.data.data || []);
    } catch (err) {
      console.error('Failed to load templates:', err);
    }
  };

  const fetchMarketStats = async (city: string) => {
    setMarketLoading(true);
    try {
      const res = await axiosInstance.get('/marketing/market-stats', { params: { city } });
      setMarketStats(res.data.data || null);
    } catch (err) {
      console.error('Failed to load market stats:', err);
      setMarketStats(null);
    }
    setMarketLoading(false);
  };

  const fetchCities = async () => {
    try {
      const res = await axiosInstance.get('/marketing/market-stats/cities');
      setCities(res.data.data || []);
    } catch (err) {
      console.error('Failed to load cities:', err);
    }
  };

  const fetchSubdivisions = async (city: string) => {
    setSubdivisionsLoading(true);
    try {
      const res = await axiosInstance.get('/marketing/market-stats/subdivisions', { params: { city } });
      setSubdivisions(res.data.data?.subdivisions || []);
    } catch (err) {
      console.error('Failed to load subdivisions:', err);
      setSubdivisions([]);
    }
    setSubdivisionsLoading(false);
  };

  const fetchHistory = async () => {
    setHistoryLoading(true);
    try {
      const res = await axiosInstance.get('/marketing/history', { params: { limit: 50 } });
      setHistory(res.data.data || []);
    } catch (err) {
      console.error('Failed to load history:', err);
    }
    setHistoryLoading(false);
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  useEffect(() => {
    if (activeTab === 'kits' || activeTab === 'library') fetchListings();
  }, [activeTab, scope, statusFilter, fetchListings]);

  useEffect(() => {
    if (activeTab === 'market') fetchCities();
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'history') fetchHistory();
  }, [activeTab]);

  // ─── WIZARD ACTIONS ──────────────────────────────────────────

  const openWizard = (listing?: Listing, contentType?: string, opts?: { city?: string; subdivision?: string; maxPrice?: string }) => {
    setSelectedListing(listing || null);
    setSelectedContentType(contentType || 'just-listed');
    setSelectedTemplate(null);
    setSelectedTheme('modern-dark');
    setSelectedLayout('social-square');
    setCaption('');
    setGeneratedImage('');
    setPortalLink('');
    setShowFbAdForm(false);
    setFbAdResult(null);
    if (opts?.city) setMarketCity(opts.city);
    if (opts?.subdivision) setSelectedSubdivision(opts.subdivision);
    if (opts?.maxPrice) setMaxPriceFilter(opts.maxPrice);
    setWizardStep(contentType === 'market-search' ? 2 : 1); // Skip step 1 if coming from Market Reports
    setWizardOpen(true);
  };

  const generateContent = async () => {
    if (!selectedTemplate) return;
    setGenerating(true);
    try {
      const res = await axiosInstance.post('/marketing/generate', {
        listingId: selectedListing?.listing_id,
        contentType: selectedContentType,
        templateId: selectedTemplate.id,
        theme: selectedTheme,
        layout: selectedLayout,
        city: marketCity || undefined,
        subdivision: selectedSubdivision || undefined,
        maxPrice: maxPriceFilter || undefined,
      });
      const data = res.data.data;
      setGeneratedImage(data.imageUrl);
      setCaption(data.caption || '');
      setPortalLink(data.portalLink || '');
      setWizardStep(4);
    } catch (err: any) {
      alert('Failed to generate content: ' + (err.response?.data?.message || err.message));
    }
    setGenerating(false);
  };

  const regenerateCaption = async (platform: string) => {
    setCaptionLoading(true);
    setCaptionPlatform(platform);
    try {
      const res = await axiosInstance.post('/marketing/caption', {
        listingId: selectedListing?.listing_id,
        contentType: selectedContentType,
        platform,
        city: marketCity || undefined,
      });
      setCaption(res.data.data.caption || '');
    } catch (err) {
      console.error('Caption generation failed:', err);
    }
    setCaptionLoading(false);
  };

  const copyCaption = () => {
    navigator.clipboard.writeText(caption);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const downloadImage = () => {
    if (!generatedImage) return;
    const a = document.createElement('a');
    a.href = generatedImage;
    a.download = `kairosiq-${selectedContentType}-${Date.now()}.png`;
    a.click();
  };

  const openFbAdForm = async () => {
    setShowFbAdForm(true);
    setFbAdResult(null);
    try {
      const res = await axiosInstance.get('/facebook/accounts');
      const accounts = res.data?.data || [];
      setFbAdAccounts(accounts);
      if (accounts.length > 0) setSelectedFbAccount(accounts[0].fb_account_id);
    } catch (err) {
      setFbAdAccounts([]);
    }
  };

  const publishToFacebookAds = async () => {
    if (!selectedFbAccount || !generatedImage) return;
    setFbAdPublishing(true);
    setFbAdResult(null);
    try {
      const res = await axiosInstance.post(`/facebook/accounts/${selectedFbAccount}/campaigns/create`, {
        imageUrl: generatedImage,
        caption: caption,
        dailyBudget: parseFloat(fbDailyBudget) || 10,
        durationDays: parseInt(fbDurationDays) || 7,
      });
      setFbAdResult({ success: true, data: res.data?.data, message: res.data?.message || 'Campaign created!' });
    } catch (err: any) {
      setFbAdResult({ success: false, message: err.response?.data?.message || err.message || 'Failed to create campaign' });
    }
    setFbAdPublishing(false);
  };

  const formatPrice = (price: number) => {
    if (!price) return '$0';
    return '$' + Number(price).toLocaleString('en-US', { maximumFractionDigits: 0 });
  };

  const contentTypes = [
    { id: 'just-listed', label: 'Just Listed', icon: faTag },
    { id: 'just-sold', label: 'Just Sold', icon: faCheck },
    { id: 'open-house', label: 'Open House', icon: faCalendarAlt },
    { id: 'market-stats', label: 'Market Stats', icon: faChartBar },
    { id: 'price-reduced', label: 'Price Reduced', icon: faDollarSign },
    { id: 'market-search', label: 'Area Spotlight', icon: faGlobe },
  ];

  // ─── RENDER: TABS ────────────────────────────────────────────

  const tabDefs = [
    { id: 'library' as TabType, label: 'Content Library', icon: faImage },
    { id: 'kits' as TabType, label: 'Marketing Kits', icon: faBullhorn },
    { id: 'market' as TabType, label: 'Market Reports', icon: faChartBar },
    { id: 'history' as TabType, label: 'My Posts', icon: faHistory },
    { id: 'leads' as TabType, label: 'Leads', icon: faUserPlus },
  ];

  // ─── RENDER: MARKETING KITS TAB ─────────────────────────────

  const renderKitsTab = () => (
    <div>
      {/* Scope toggle */}
      <div style={styles.scopeToggle}>
        {[
          { id: 'mine' as ScopeType, label: 'My Listings', icon: faUser },
          { id: 'brokerage' as ScopeType, label: 'Brokerage Listings', icon: faBuilding },
          { id: 'search' as ScopeType, label: 'Search Any', icon: faGlobe },
        ].map(s => (
          <button
            key={s.id}
            style={{ ...styles.scopeBtn, ...(scope === s.id ? styles.scopeBtnActive : {}) }}
            onClick={() => { setScope(s.id); setSearchQuery(''); }}
          >
            <FontAwesomeIcon icon={s.icon} />
            {s.label}
          </button>
        ))}
      </div>

      {/* Search bar for "Search Any" scope */}
      {scope === 'search' && (
        <div style={styles.searchBox}>
          <input
            style={styles.searchInput}
            placeholder="Search by address or MLS#..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && fetchListings()}
          />
          <button style={styles.generateBtn} onClick={fetchListings}>
            <FontAwesomeIcon icon={faSearch} /> Search
          </button>
        </div>
      )}

      {/* Status filter */}
      <div style={styles.statusFilter}>
        {['Active', 'Pending', 'Closed', 'all'].map(st => (
          <button
            key={st}
            style={{ ...styles.statusChip, ...(statusFilter === st ? styles.statusChipActive : {}) }}
            onClick={() => setStatusFilter(st)}
          >
            {st === 'all' ? 'All' : st}
          </button>
        ))}
        <span style={{ marginLeft: 'auto', fontSize: '13px', color: MUTED }}>
          {listingsTotal} listings
        </span>
      </div>

      {/* Listings list */}
      <div style={styles.card}>
        {listingsLoading ? (
          <div style={styles.emptyState}>
            <FontAwesomeIcon icon={faSpinner} spin style={{ fontSize: '24px', color: GOLD }} />
            <div style={{ marginTop: '12px' }}>Loading listings...</div>
          </div>
        ) : listings.length === 0 ? (
          <div style={styles.emptyState}>
            <FontAwesomeIcon icon={faHome} style={{ fontSize: '40px', marginBottom: '12px' }} />
            <div style={{ fontSize: '16px', fontWeight: 600, color: NAVY }}>No listings found</div>
            <div style={{ marginTop: '8px' }}>
              {scope === 'mine' ? 'You don\'t have any active listings in MLS' :
               scope === 'brokerage' ? 'No listings found for your brokerage' :
               'Try searching by address or MLS number'}
            </div>
          </div>
        ) : (
          listings.map(listing => (
            <div
              key={listing.listing_id}
              style={styles.listingRow}
              onMouseEnter={(e) => (e.currentTarget.style.background = '#f9fafb')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
            >
              {listing.photo_url ? (
                <img src={listing.photo_url} alt="" style={styles.listingPhoto} />
              ) : (
                <div style={{ ...styles.listingPhoto, display: 'flex', alignItems: 'center', justifyContent: 'center', color: MUTED, fontSize: '11px' }}>
                  No Photo
                </div>
              )}
              <div style={styles.listingInfo}>
                <div style={styles.listingAddress}>{listing.unparsed_address}</div>
                <div style={styles.listingMeta}>
                  {listing.city}, {listing.state} {listing.zip_code} &middot;
                  {listing.bedrooms}bd/{listing.bathrooms}ba &middot;
                  {Number(listing.living_area).toLocaleString()} sqft &middot;
                  {listing.days_on_market} DOM
                </div>
                <div style={styles.listingMeta}>
                  Listed by: {listing.list_agent_name} &middot; {listing.list_office_name}
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={styles.listingPrice}>{formatPrice(listing.list_price)}</div>
                <span style={{
                  display: 'inline-block',
                  padding: '2px 10px',
                  borderRadius: '10px',
                  fontSize: '11px',
                  fontWeight: 700,
                  background: listing.standard_status === 'Active' ? '#dcfce7' : listing.standard_status === 'Pending' ? '#fef3c7' : '#f3f4f6',
                  color: listing.standard_status === 'Active' ? GREEN : listing.standard_status === 'Pending' ? '#92400e' : '#6b7280',
                }}>
                  {listing.standard_status}
                </span>
              </div>
              <button
                style={styles.generateBtn}
                onClick={() => openWizard(listing, listing.standard_status === 'Closed' ? 'just-sold' : 'just-listed')}
              >
                <FontAwesomeIcon icon={faMagic} /> Create Post
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );

  // ─── RENDER: CONTENT LIBRARY TAB ────────────────────────────

  const renderLibraryTab = () => (
    <div>
      <div style={styles.cardTitle}>Choose a Content Type</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '16px' }}>
        {contentTypes.map(ct => (
          <div
            key={ct.id}
            style={{
              ...styles.card,
              cursor: 'pointer',
              textAlign: 'center',
              padding: '32px 20px',
              transition: 'all 0.2s',
              border: `2px solid #e2e8f0`,
            }}
            onClick={() => { setSelectedContentType(ct.id); setActiveTab('kits'); }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = GOLD; e.currentTarget.style.transform = 'translateY(-2px)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.transform = 'none'; }}
          >
            <FontAwesomeIcon icon={ct.icon} style={{ fontSize: '32px', color: GOLD, marginBottom: '12px' }} />
            <div style={{ fontWeight: 700, color: NAVY, fontSize: '16px' }}>{ct.label}</div>
            <div style={{ fontSize: '12px', color: MUTED, marginTop: '6px' }}>
              {templates.filter(t => t.contentType === ct.id).length} templates available
            </div>
          </div>
        ))}
      </div>

      <div style={{ ...styles.cardTitle, marginTop: '32px' }}>Recent Listings Ready for Marketing</div>
      <div style={styles.card}>
        {listings.slice(0, 5).map(listing => (
          <div
            key={listing.listing_id}
            style={{ ...styles.listingRow, cursor: 'pointer' }}
            onClick={() => openWizard(listing)}
          >
            {listing.photo_url ? (
              <img src={listing.photo_url} alt="" style={styles.listingPhoto} />
            ) : (
              <div style={{ ...styles.listingPhoto, display: 'flex', alignItems: 'center', justifyContent: 'center', color: MUTED, fontSize: '11px' }}>No Photo</div>
            )}
            <div style={styles.listingInfo}>
              <div style={styles.listingAddress}>{listing.unparsed_address}</div>
              <div style={styles.listingMeta}>{listing.city} &middot; {formatPrice(listing.list_price)}</div>
            </div>
            <button style={styles.generateBtn} onClick={(e) => { e.stopPropagation(); openWizard(listing); }}>
              <FontAwesomeIcon icon={faMagic} /> Create Post
            </button>
          </div>
        ))}
      </div>
    </div>
  );

  // ─── RENDER: MARKET REPORTS TAB ──────────────────────────────

  const renderMarketTab = () => (
    <div>
      {/* Filters */}
      <div style={styles.card}>
        <div style={styles.cardTitle}>Market Report Generator</div>
        <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', flexWrap: 'wrap' as const }}>
          <select
            style={{ ...styles.searchInput, maxWidth: '250px' }}
            value={marketCity}
            onChange={(e) => {
              setMarketCity(e.target.value);
              setSelectedSubdivision('');
              setSubdivisions([]);
              if (e.target.value) {
                fetchMarketStats(e.target.value);
                fetchSubdivisions(e.target.value);
              }
            }}
          >
            <option value="">Select a city...</option>
            {cities.map(c => (
              <option key={c.city} value={c.city}>{c.city} ({c.listing_count})</option>
            ))}
          </select>

          {subdivisions.length > 0 && (
            <select
              style={{ ...styles.searchInput, maxWidth: '250px' }}
              value={selectedSubdivision}
              onChange={(e) => setSelectedSubdivision(e.target.value)}
            >
              <option value="">All Subdivisions</option>
              {subdivisions.map((s: any) => (
                <option key={s.subdivision_name} value={s.subdivision_name}>
                  {s.subdivision_name} ({s.active_count})
                </option>
              ))}
            </select>
          )}

          <select
            style={{ ...styles.searchInput, maxWidth: '180px' }}
            value={maxPriceFilter}
            onChange={(e) => setMaxPriceFilter(e.target.value)}
          >
            <option value="">Any Price</option>
            <option value="300000">Under $300K</option>
            <option value="400000">Under $400K</option>
            <option value="500000">Under $500K</option>
            <option value="550000">Under $550K</option>
            <option value="650000">Under $650K</option>
            <option value="800000">Under $800K</option>
            <option value="1000000">Under $1M</option>
          </select>

          {marketCity && (
            <button
              style={styles.generateBtn}
              onClick={() => openWizard(undefined, 'market-search', {
                city: marketCity,
                subdivision: selectedSubdivision,
                maxPrice: maxPriceFilter,
              })}
            >
              <FontAwesomeIcon icon={faMagic} /> Create Market Post
            </button>
          )}
        </div>
      </div>

      {/* City Stats */}
      {marketLoading ? (
        <div style={{ ...styles.card, ...styles.emptyState }}>
          <FontAwesomeIcon icon={faSpinner} spin style={{ fontSize: '24px', color: GOLD }} />
          <div style={{ marginTop: '12px' }}>Loading market data...</div>
        </div>
      ) : marketStats ? (
        <div style={styles.card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h3 style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '24px', color: NAVY, margin: 0 }}>
              {marketStats.location} <span style={{ color: GOLD }}>Market Overview</span>
            </h3>
            <button
              style={{ ...styles.generateBtn, fontSize: '12px', padding: '6px 16px' }}
              onClick={() => openWizard(undefined, 'market-stats', { city: marketCity })}
            >
              <FontAwesomeIcon icon={faMagic} /> Stats Shareable
            </button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '12px' }}>
            <div style={styles.statCard}>
              <div style={styles.statValue}>{formatPrice(marketStats.active?.median_price)}</div>
              <div style={styles.statLabel}>Median Price</div>
            </div>
            <div style={styles.statCard}>
              <div style={styles.statValue}>{marketStats.active?.active_count || 0}</div>
              <div style={styles.statLabel}>Active Listings</div>
            </div>
            <div style={styles.statCard}>
              <div style={styles.statValue}>{marketStats.active?.avg_dom || 0}</div>
              <div style={styles.statLabel}>Avg DOM</div>
            </div>
            <div style={styles.statCard}>
              <div style={styles.statValue}>{marketStats.closed?.closed_count || 0}</div>
              <div style={styles.statLabel}>Sold (30d)</div>
            </div>
            {marketStats.closed?.sp_lp_ratio ? (
              <div style={styles.statCard}>
                <div style={styles.statValue}>{marketStats.closed.sp_lp_ratio}%</div>
                <div style={styles.statLabel}>SP/LP Ratio</div>
              </div>
            ) : null}
            <div style={styles.statCard}>
              <div style={styles.statValue}>{marketStats.newListings}</div>
              <div style={styles.statLabel}>New (7d)</div>
            </div>
          </div>
        </div>
      ) : (
        <div style={{ ...styles.card, ...styles.emptyState }}>
          <FontAwesomeIcon icon={faChartBar} style={{ fontSize: '40px', marginBottom: '12px' }} />
          <div style={{ fontSize: '16px', fontWeight: 600, color: NAVY }}>Select a city to view market stats</div>
          <div style={{ marginTop: '8px' }}>Generate shareable market reports and area spotlight posts from live MLS data</div>
        </div>
      )}

      {/* Subdivisions */}
      {subdivisions.length > 0 && (
        <div style={styles.card}>
          <div style={styles.cardTitle}>Subdivisions in {marketCity}</div>
          <div style={{ fontSize: '13px', color: MUTED, marginBottom: '16px' }}>
            Click "Create Post" to generate an area spotlight for any subdivision — drives consumers to your AI portal.
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: `2px solid ${GOLD}`, textAlign: 'left' }}>
                <th style={{ padding: '8px 12px', fontSize: '11px', color: MUTED, textTransform: 'uppercase', letterSpacing: '1px' }}>Subdivision</th>
                <th style={{ padding: '8px 12px', fontSize: '11px', color: MUTED, textTransform: 'uppercase', letterSpacing: '1px', textAlign: 'center' }}>Listings</th>
                <th style={{ padding: '8px 12px', fontSize: '11px', color: MUTED, textTransform: 'uppercase', letterSpacing: '1px', textAlign: 'right' }}>Avg Price</th>
                <th style={{ padding: '8px 12px', fontSize: '11px', color: MUTED, textTransform: 'uppercase', letterSpacing: '1px', textAlign: 'center' }}>Avg DOM</th>
                <th style={{ padding: '8px 12px', fontSize: '11px', color: MUTED, textTransform: 'uppercase', letterSpacing: '1px', textAlign: 'center' }}>Avg SqFt</th>
                <th style={{ padding: '8px 12px' }}></th>
              </tr>
            </thead>
            <tbody>
              {subdivisions.map((sub: any) => (
                <tr
                  key={sub.subdivision_name}
                  style={{ borderBottom: '1px solid #f0f0f0' }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = '#f9fafb')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                >
                  <td style={{ padding: '10px 12px', fontWeight: 600, color: NAVY, fontSize: '14px' }}>
                    {sub.subdivision_name}
                  </td>
                  <td style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 700, color: GOLD, fontSize: '15px' }}>
                    {sub.active_count}
                  </td>
                  <td style={{ padding: '10px 12px', textAlign: 'right', fontFamily: "'Bebas Neue', sans-serif", fontSize: '18px', color: NAVY }}>
                    {formatPrice(sub.avg_price)}
                  </td>
                  <td style={{ padding: '10px 12px', textAlign: 'center', color: '#6b7280' }}>
                    {sub.avg_dom} days
                  </td>
                  <td style={{ padding: '10px 12px', textAlign: 'center', color: '#6b7280' }}>
                    {Number(sub.avg_sqft || 0).toLocaleString()}
                  </td>
                  <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                    <button
                      style={{ ...styles.generateBtn, fontSize: '11px', padding: '5px 14px' }}
                      onClick={() => openWizard(undefined, 'market-search', {
                        city: marketCity,
                        subdivision: sub.subdivision_name,
                      })}
                    >
                      <FontAwesomeIcon icon={faMagic} /> Create Post
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {subdivisionsLoading && (
        <div style={{ ...styles.card, textAlign: 'center', padding: '30px', color: MUTED }}>
          <FontAwesomeIcon icon={faSpinner} spin /> Loading subdivisions...
        </div>
      )}
    </div>
  );

  // ─── RENDER: HISTORY TAB ─────────────────────────────────────

  const renderHistoryTab = () => (
    <div>
      <div style={styles.card}>
        <div style={styles.cardTitle}>Generated Content History</div>
        {historyLoading ? (
          <div style={styles.emptyState}>
            <FontAwesomeIcon icon={faSpinner} spin style={{ fontSize: '24px', color: GOLD }} />
          </div>
        ) : history.length === 0 ? (
          <div style={styles.emptyState}>
            <FontAwesomeIcon icon={faHistory} style={{ fontSize: '40px', marginBottom: '12px' }} />
            <div style={{ fontSize: '16px', fontWeight: 600, color: NAVY }}>No posts yet</div>
            <div style={{ marginTop: '8px' }}>Create your first marketing post to see it here</div>
          </div>
        ) : (
          history.map(item => (
            <div key={item.id} style={styles.historyRow}>
              {item.image_url && (
                <img src={item.image_url} alt="" style={styles.historyThumb} />
              )}
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, fontSize: '14px', color: NAVY }}>
                  {item.content_type.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </div>
                <div style={{ fontSize: '13px', color: '#6b7280' }}>
                  {item.listing_address || item.city || 'Market Report'}
                  {item.listing_price ? ` - ${formatPrice(item.listing_price)}` : ''}
                </div>
                <div style={{ fontSize: '11px', color: MUTED, marginTop: '2px' }}>
                  {new Date(item.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}
                </div>
              </div>
              {item.lead_count > 0 && (
                <span style={{ padding: '4px 12px', borderRadius: '12px', background: '#dcfce7', color: GREEN, fontSize: '12px', fontWeight: 700 }}>
                  {item.lead_count} leads
                </span>
              )}
              <button style={{ ...styles.shareBtn, fontSize: '12px', padding: '6px 12px' }} onClick={() => window.open(item.image_url, '_blank')}>
                <FontAwesomeIcon icon={faDownload} /> Download
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );

  // ─── RENDER: LEADS TAB ───────────────────────────────────────

  const renderLeadsTab = () => (
    <div style={styles.card}>
      <div style={styles.cardTitle}>Marketing-Sourced Leads</div>
      <div style={styles.emptyState}>
        <FontAwesomeIcon icon={faUserPlus} style={{ fontSize: '40px', marginBottom: '12px' }} />
        <div style={{ fontSize: '16px', fontWeight: 600, color: NAVY }}>Lead Attribution Coming Soon</div>
        <div style={{ marginTop: '8px' }}>
          When consumers click your shared posts and land on aisearchportal.com,<br />
          those leads will appear here with full source attribution.
        </div>
      </div>
    </div>
  );

  // ─── RENDER: CREATE POST WIZARD ──────────────────────────────

  const renderWizard = () => {
    if (!wizardOpen) return null;

    const filteredTemplates = templates.filter(t => t.contentType === selectedContentType);

    return (
      <div style={styles.modalOverlay} onClick={() => setWizardOpen(false)}>
        <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
          {/* Header */}
          <div style={styles.modalHeader}>
            <span style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '22px', color: '#fff', letterSpacing: '1px' }}>
              Create Marketing Post
            </span>
            <button onClick={() => setWizardOpen(false)} style={{ background: 'none', border: 'none', color: MUTED, cursor: 'pointer', fontSize: '20px' }}>
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>

          <div style={styles.modalBody}>
            {/* Step indicators */}
            <div style={styles.stepIndicator}>
              {[1, 2, 3, 4].map(step => (
                <React.Fragment key={step}>
                  <div style={{
                    ...styles.stepDot,
                    ...(wizardStep === step ? styles.stepDotActive : {}),
                    ...(wizardStep > step ? styles.stepDotComplete : {}),
                  }}>
                    {wizardStep > step ? <FontAwesomeIcon icon={faCheck} /> : step}
                  </div>
                  {step < 4 && <div style={{ flex: 1, height: '2px', background: wizardStep > step ? GREEN : '#e2e8f0', alignSelf: 'center' }} />}
                </React.Fragment>
              ))}
            </div>

            {/* Step 1: Content Type */}
            {wizardStep === 1 && (
              <div>
                <h3 style={{ marginBottom: '16px', color: NAVY }}>1. What would you like to share?</h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '12px' }}>
                  {contentTypes.map(ct => (
                    <div
                      key={ct.id}
                      style={{
                        ...styles.templateCard,
                        ...(selectedContentType === ct.id ? styles.templateCardActive : {}),
                        padding: '20px 12px',
                      }}
                      onClick={() => setSelectedContentType(ct.id)}
                    >
                      <FontAwesomeIcon icon={ct.icon} style={{ fontSize: '24px', color: selectedContentType === ct.id ? GOLD : MUTED, marginBottom: '8px' }} />
                      <div style={{ fontWeight: 600, fontSize: '13px', color: NAVY }}>{ct.label}</div>
                    </div>
                  ))}
                </div>
                <div style={{ marginTop: '24px', textAlign: 'right' }}>
                  <button style={styles.generateBtn} onClick={() => setWizardStep(2)}>
                    Next <FontAwesomeIcon icon={faChevronRight} />
                  </button>
                </div>
              </div>
            )}

            {/* Step 2: Select template */}
            {wizardStep === 2 && (
              <div>
                <h3 style={{ marginBottom: '16px', color: NAVY }}>2. Select a Template</h3>

                {selectedListing && (
                  <div style={{ ...styles.card, background: '#f9fafb', display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '16px' }}>
                    {selectedListing.photo_url && <img src={selectedListing.photo_url} alt="" style={{ width: '80px', height: '56px', borderRadius: '6px', objectFit: 'cover' }} />}
                    <div>
                      <div style={{ fontWeight: 700, color: NAVY }}>{selectedListing.unparsed_address}</div>
                      <div style={{ fontSize: '13px', color: '#6b7280' }}>{selectedListing.city} &middot; {formatPrice(selectedListing.list_price)}</div>
                    </div>
                  </div>
                )}

                <div style={styles.templateGrid}>
                  {filteredTemplates.length > 0 ? filteredTemplates.map(t => (
                    <div
                      key={t.id}
                      style={{
                        ...styles.templateCard,
                        ...(selectedTemplate?.id === t.id ? styles.templateCardActive : {}),
                      }}
                      onClick={() => setSelectedTemplate(t)}
                    >
                      <div style={{
                        width: '100%',
                        height: '100px',
                        background: NAVY,
                        borderRadius: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginBottom: '8px',
                      }}>
                        <span style={{ color: GOLD2, fontFamily: "'Bebas Neue', sans-serif", fontSize: '14px' }}>{t.name}</span>
                      </div>
                      <div style={{ fontSize: '12px', fontWeight: 600, color: NAVY }}>{t.name}</div>
                    </div>
                  )) : (
                    <div style={{ gridColumn: '1 / -1', ...styles.emptyState }}>
                      No templates available for this content type yet.
                    </div>
                  )}
                </div>

                {/* Theme selector */}
                <div style={{ marginTop: '20px' }}>
                  <div style={{ fontWeight: 600, fontSize: '14px', color: NAVY, marginBottom: '8px' }}>Theme</div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    {['modern-dark', 'modern-light'].map(theme => (
                      <button
                        key={theme}
                        style={{
                          ...styles.scopeBtn,
                          ...(selectedTheme === theme ? styles.scopeBtnActive : {}),
                        }}
                        onClick={() => setSelectedTheme(theme)}
                      >
                        {theme === 'modern-dark' ? 'Dark' : 'Light'}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Layout selector */}
                <div style={{ marginTop: '16px' }}>
                  <div style={{ fontWeight: 600, fontSize: '14px', color: NAVY, marginBottom: '8px' }}>Format</div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    {['social-square', 'social-story', 'social-landscape'].map(layout => (
                      <button
                        key={layout}
                        style={{
                          ...styles.scopeBtn,
                          ...(selectedLayout === layout ? styles.scopeBtnActive : {}),
                        }}
                        onClick={() => setSelectedLayout(layout)}
                      >
                        {layout === 'social-square' ? 'Square (1:1)' : layout === 'social-story' ? 'Story (9:16)' : 'Landscape (2:1)'}
                      </button>
                    ))}
                  </div>
                </div>

                <div style={{ marginTop: '24px', display: 'flex', justifyContent: 'space-between' }}>
                  <button style={{ ...styles.shareBtn }} onClick={() => setWizardStep(1)}>
                    <FontAwesomeIcon icon={faChevronLeft} /> Back
                  </button>
                  <button
                    style={{ ...styles.generateBtn, opacity: selectedTemplate ? 1 : 0.5 }}
                    onClick={() => selectedTemplate && setWizardStep(3)}
                    disabled={!selectedTemplate}
                  >
                    Next <FontAwesomeIcon icon={faChevronRight} />
                  </button>
                </div>
              </div>
            )}

            {/* Step 3: Generate */}
            {wizardStep === 3 && (
              <div>
                <h3 style={{ marginBottom: '16px', color: NAVY }}>3. Generate Content</h3>
                <div style={{ ...styles.card, background: '#f9fafb' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', fontSize: '14px' }}>
                    <div><strong>Type:</strong> {selectedContentType.replace('-', ' ')}</div>
                    <div><strong>Template:</strong> {selectedTemplate?.name}</div>
                    <div><strong>Theme:</strong> {selectedTheme}</div>
                    <div><strong>Format:</strong> {selectedLayout}</div>
                    {selectedListing && (
                      <div style={{ gridColumn: '1 / -1' }}><strong>Listing:</strong> {selectedListing.unparsed_address} — {formatPrice(selectedListing.list_price)}</div>
                    )}
                  </div>
                </div>

                <div style={{ marginTop: '24px', display: 'flex', justifyContent: 'space-between' }}>
                  <button style={styles.shareBtn} onClick={() => setWizardStep(2)}>
                    <FontAwesomeIcon icon={faChevronLeft} /> Back
                  </button>
                  <button
                    style={{ ...styles.generateBtn, padding: '12px 32px', fontSize: '16px' }}
                    onClick={generateContent}
                    disabled={generating}
                  >
                    {generating ? (
                      <><FontAwesomeIcon icon={faSpinner} spin /> Generating...</>
                    ) : (
                      <><FontAwesomeIcon icon={faMagic} /> Generate Post</>
                    )}
                  </button>
                </div>
              </div>
            )}

            {/* Step 4: Preview + Share */}
            {wizardStep === 4 && (
              <div>
                <h3 style={{ marginBottom: '16px', color: NAVY }}>4. Your Post is Ready!</h3>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
                  {/* Preview */}
                  <div>
                    {generatedImage && (
                      <img
                        src={generatedImage}
                        alt="Generated marketing post"
                        style={{ width: '100%', borderRadius: '8px', boxShadow: '0 4px 20px rgba(0,0,0,0.15)' }}
                      />
                    )}
                  </div>

                  {/* Caption + Actions */}
                  <div>
                    <div style={{ fontWeight: 600, fontSize: '14px', color: NAVY, marginBottom: '8px' }}>
                      Caption ({captionPlatform})
                    </div>

                    {/* Platform toggle */}
                    <div style={{ display: 'flex', gap: '6px', marginBottom: '12px' }}>
                      {['instagram', 'facebook', 'linkedin'].map(p => (
                        <button
                          key={p}
                          style={{
                            ...styles.statusChip,
                            ...(captionPlatform === p ? styles.statusChipActive : {}),
                          }}
                          onClick={() => regenerateCaption(p)}
                          disabled={captionLoading}
                        >
                          {p.charAt(0).toUpperCase() + p.slice(1)}
                        </button>
                      ))}
                    </div>

                    <div style={styles.captionBox}>
                      {captionLoading ? (
                        <div style={{ textAlign: 'center', color: MUTED }}>
                          <FontAwesomeIcon icon={faSpinner} spin /> Generating caption...
                        </div>
                      ) : caption}
                    </div>

                    <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                      <button style={{ ...styles.shareBtn, flex: 1, justifyContent: 'center' }} onClick={copyCaption}>
                        <FontAwesomeIcon icon={copied ? faCheck : faCopy} />
                        {copied ? 'Copied!' : 'Copy Caption'}
                      </button>
                      <button style={{ ...styles.shareBtn, flex: 1, justifyContent: 'center' }} onClick={() => regenerateCaption(captionPlatform)}>
                        <FontAwesomeIcon icon={faRedo} /> Regenerate
                      </button>
                    </div>

                    {/* Portal Link */}
                    {portalLink && (
                      <div style={{ marginTop: '16px', padding: '12px 16px', background: '#f0fdf4', border: `1px solid ${GREEN}`, borderRadius: '8px' }}>
                        <div style={{ fontSize: '12px', fontWeight: 700, color: GREEN, marginBottom: '4px' }}>AI Portal Link (included in caption)</div>
                        <div style={{ fontSize: '13px', color: NAVY, wordBreak: 'break-all' as const }}>{portalLink}</div>
                        <button
                          style={{ ...styles.statusChip, marginTop: '8px', background: GREEN, color: '#fff', borderColor: GREEN }}
                          onClick={() => { navigator.clipboard.writeText(portalLink); }}
                        >
                          Copy Link
                        </button>
                      </div>
                    )}

                    <div style={{ marginTop: '20px', fontWeight: 600, fontSize: '14px', color: NAVY, marginBottom: '8px' }}>
                      Download & Share
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      <button style={{ ...styles.generateBtn, justifyContent: 'center', width: '100%' }} onClick={downloadImage}>
                        <FontAwesomeIcon icon={faDownload} /> Download Image
                      </button>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                          style={{ ...styles.shareBtn, flex: 1, justifyContent: 'center', background: '#1877f2', color: '#fff', borderColor: '#1877f2' }}
                          onClick={() => {
                            const fbUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(portalLink)}&quote=${encodeURIComponent(caption)}`;
                            window.open(fbUrl, '_blank', 'width=600,height=400');
                          }}
                        >
                          <FontAwesomeIcon icon={faFacebookBrand} /> Facebook
                        </button>
                        <button
                          style={{ ...styles.shareBtn, flex: 1, justifyContent: 'center', background: 'linear-gradient(45deg, #f09433, #e6683c, #dc2743, #cc2366, #bc1888)', color: '#fff', borderColor: 'transparent' }}
                          onClick={() => {
                            // Instagram doesn't support direct sharing via URL — download image + copy caption
                            downloadImage();
                            navigator.clipboard.writeText(caption);
                            alert('Image downloaded & caption copied! Open Instagram and paste the caption with your post.');
                          }}
                        >
                          <FontAwesomeIcon icon={faInstagramBrand} /> Instagram
                        </button>
                        <button
                          style={{ ...styles.shareBtn, flex: 1, justifyContent: 'center', background: '#0077b5', color: '#fff', borderColor: '#0077b5' }}
                          onClick={() => {
                            const liUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(portalLink)}`;
                            window.open(liUrl, '_blank', 'width=600,height=400');
                          }}
                        >
                          <FontAwesomeIcon icon={faLinkedinBrand} /> LinkedIn
                        </button>
                      </div>

                      {/* ── Publish as Facebook Ad ── */}
                      <div style={{ marginTop: '16px', borderTop: `1px solid #e2e8f0`, paddingTop: '16px' }}>
                        {!showFbAdForm ? (
                          <button
                            style={{ ...styles.generateBtn, justifyContent: 'center', width: '100%', background: 'linear-gradient(135deg, #1877f2, #0d5bbd)' }}
                            onClick={openFbAdForm}
                          >
                            <FontAwesomeIcon icon={faFacebookBrand} /> Publish as Facebook Ad
                          </button>
                        ) : (
                          <div style={{ background: '#f8fafc', border: `1px solid #e2e8f0`, borderRadius: '10px', padding: '16px' }}>
                            <div style={{ fontWeight: 700, fontSize: '14px', color: NAVY, marginBottom: '12px' }}>
                              <FontAwesomeIcon icon={faFacebookBrand} style={{ color: '#1877f2', marginRight: '6px' }} />
                              Launch Facebook Ad Campaign
                            </div>

                            {fbAdAccounts.length === 0 ? (
                              <div style={{ textAlign: 'center', padding: '16px', color: MUTED }}>
                                <div style={{ marginBottom: '8px' }}>No Facebook Ad accounts connected.</div>
                                <button
                                  style={{ ...styles.shareBtn, background: '#1877f2', color: '#fff', borderColor: '#1877f2' }}
                                  onClick={() => window.location.href = '/admin/facebook-ads'}
                                >
                                  Connect Account
                                </button>
                              </div>
                            ) : (
                              <>
                                {/* Account selector */}
                                {fbAdAccounts.length > 1 && (
                                  <div style={{ marginBottom: '10px' }}>
                                    <label style={{ fontSize: '12px', fontWeight: 600, color: NAVY, display: 'block', marginBottom: '4px' }}>Ad Account</label>
                                    <select
                                      value={selectedFbAccount}
                                      onChange={(e) => setSelectedFbAccount(e.target.value)}
                                      style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: `1px solid #e2e8f0`, fontSize: '13px' }}
                                    >
                                      {fbAdAccounts.map((acc: any) => (
                                        <option key={acc.fb_account_id} value={acc.fb_account_id}>
                                          {acc.account_name || acc.fb_account_id}
                                        </option>
                                      ))}
                                    </select>
                                  </div>
                                )}

                                {/* Budget + Duration */}
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '12px' }}>
                                  <div>
                                    <label style={{ fontSize: '12px', fontWeight: 600, color: NAVY, display: 'block', marginBottom: '4px' }}>Daily Budget ($)</label>
                                    <input
                                      type="number"
                                      value={fbDailyBudget}
                                      onChange={(e) => setFbDailyBudget(e.target.value)}
                                      min="1"
                                      max="10000"
                                      style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: `1px solid #e2e8f0`, fontSize: '13px' }}
                                    />
                                  </div>
                                  <div>
                                    <label style={{ fontSize: '12px', fontWeight: 600, color: NAVY, display: 'block', marginBottom: '4px' }}>Duration (days)</label>
                                    <input
                                      type="number"
                                      value={fbDurationDays}
                                      onChange={(e) => setFbDurationDays(e.target.value)}
                                      min="1"
                                      max="90"
                                      style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: `1px solid #e2e8f0`, fontSize: '13px' }}
                                    />
                                  </div>
                                </div>

                                <div style={{ fontSize: '11px', color: MUTED, marginBottom: '12px' }}>
                                  Est. total: ${((parseFloat(fbDailyBudget) || 0) * (parseInt(fbDurationDays) || 0)).toFixed(2)} &middot; Campaign launches in PAUSED state for your review
                                </div>

                                {/* Result message */}
                                {fbAdResult && (
                                  <div style={{
                                    padding: '10px 14px', borderRadius: '8px', marginBottom: '12px', fontSize: '13px',
                                    background: fbAdResult.success ? '#f0fdf4' : '#fef2f2',
                                    border: `1px solid ${fbAdResult.success ? GREEN : RED}`,
                                    color: fbAdResult.success ? GREEN : RED,
                                  }}>
                                    {fbAdResult.message}
                                  </div>
                                )}

                                <div style={{ display: 'flex', gap: '8px' }}>
                                  <button
                                    style={{ ...styles.shareBtn, flex: 1, justifyContent: 'center' }}
                                    onClick={() => setShowFbAdForm(false)}
                                  >
                                    Cancel
                                  </button>
                                  <button
                                    style={{ ...styles.generateBtn, flex: 2, justifyContent: 'center', background: fbAdPublishing ? MUTED : 'linear-gradient(135deg, #1877f2, #0d5bbd)' }}
                                    onClick={publishToFacebookAds}
                                    disabled={fbAdPublishing || !selectedFbAccount}
                                  >
                                    {fbAdPublishing ? (
                                      <><FontAwesomeIcon icon={faSpinner} spin /> Creating Campaign...</>
                                    ) : (
                                      <><FontAwesomeIcon icon={faFacebookBrand} /> Create Campaign</>
                                    )}
                                  </button>
                                </div>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // ─── MAIN RENDER ─────────────────────────────────────────────

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerTitle}>
          <FontAwesomeIcon icon={faBullhorn} style={{ color: GOLD }} />
          Marketing Hub
        </div>
        <button style={styles.generateBtn} onClick={() => openWizard()}>
          <FontAwesomeIcon icon={faMagic} /> Create Post
        </button>
      </div>

      {/* Tabs */}
      <div style={styles.tabs}>
        {tabDefs.map(tab => (
          <div
            key={tab.id}
            style={{ ...styles.tab, ...(activeTab === tab.id ? styles.tabActive : {}) }}
            onClick={() => setActiveTab(tab.id)}
          >
            <FontAwesomeIcon icon={tab.icon} />
            {tab.label}
          </div>
        ))}
      </div>

      {/* Content */}
      <div style={styles.content}>
        {activeTab === 'library' && renderLibraryTab()}
        {activeTab === 'kits' && renderKitsTab()}
        {activeTab === 'market' && renderMarketTab()}
        {activeTab === 'history' && renderHistoryTab()}
        {activeTab === 'leads' && renderLeadsTab()}
      </div>

      {/* Wizard Modal */}
      {renderWizard()}
    </div>
  );
};

export default MarketingHub;
